import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Radio, AlertCircle, CheckCircle2, FileText, Receipt as ReceiptIcon } from 'lucide-react';
import { useLiveSessionsAudit, useExpertsList } from '@/hooks/useLiveSessionsAudit';
import { Link } from 'react-router-dom';
import { useCohortFilterStore, useOrgFilterStore } from '@/hooks/useOrgFilterStore';

function monthRange(monthStr: string) {
  const [y, m] = monthStr.split('-').map(Number);
  const start = new Date(Date.UTC(y, m - 1, 1)).toISOString().slice(0, 10);
  const end = new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10);
  return { start, end };
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatTime(h: number | null, m: number | null) {
  if (h == null) return '--:--';
  return `${String(h).padStart(2, '0')}:${String(m ?? 0).padStart(2, '0')}`;
}

export default function LiveSessionsAuditPage() {
  const [month, setMonth] = useState(currentMonth());
  const [expertId, setExpertId] = useState<string>('all');
  const [orgId, setOrgId] = useOrgFilterStore();
  const [cohortId, setCohortId] = useCohortFilterStore();
  const [type, setType] = useState<'all' | 'workshop' | 'mentoring'>('all');
  const [onlyNoRec, setOnlyNoRec] = useState(false);
  const [detail, setDetail] = useState<any | null>(null);

  const { start, end } = useMemo(() => monthRange(month), [month]);
  const { data: experts } = useExpertsList();

  const { data: organizations = [] } = useQuery({
    queryKey: ['live-sessions-orgs'],
    queryFn: async () => {
      const { data, error } = await supabase.from('organizations').select('id, name').order('name');
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: cohorts = [] } = useQuery({
    queryKey: ['live-sessions-cohorts', orgId],
    queryFn: async () => {
      let q = supabase.from('cohorts').select('id, name, organization_id').order('name');
      if (orgId !== 'all') q = q.eq('organization_id', orgId);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: rows = [], isLoading } = useLiveSessionsAudit({
    monthStart: start,
    monthEnd: end,
    organizationId: orgId === 'all' ? null : orgId,
    cohortId: cohortId === 'all' ? null : cohortId,
    expertId: expertId === 'all' ? null : expertId,
    type,
    onlyNoRecording: onlyNoRec,
  });

  const kpis = useMemo(() => {
    const total = rows.length;
    const hours = rows.reduce((s, r) => s + r.duration_hours, 0);
    const withRec = rows.filter((r) => r.has_recording).length;
    const expertsCount = new Set(rows.map((r) => r.expert_id).filter(Boolean)).size;
    return { total, hours, recPct: total ? Math.round((withRec / total) * 100) : 0, expertsCount };
  }, [rows]);

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-bold flex items-center gap-2">
            <Radio className="w-7 h-7 text-primary" /> Auditoría de Sesiones en Vivo
          </h1>
          <p className="text-sm text-muted-foreground">Workshops y mentorías ya realizadas. Las sesiones sin grabación se marcan en rojo.</p>
        </div>
        <Button asChild variant="outline">
          <Link to="/payouts"><ReceiptIcon className="w-4 h-4" /> Ir a Liquidaciones</Link>
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Sesiones', value: kpis.total },
          { label: 'Horas totales', value: kpis.hours.toFixed(1) },
          { label: '% con grabación', value: `${kpis.recPct}%` },
          { label: 'Expertos activos', value: kpis.expertsCount },
        ].map((k) => (
          <Card key={k.label}>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground">{k.label}</div>
              <div className="text-2xl font-bold mt-1">{k.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs">Mes</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Organización</Label>
            <Select value={orgId} onValueChange={(v) => { setOrgId(v); setCohortId('all'); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {organizations.map((o: any) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Cohorte</Label>
            <Select value={cohortId} onValueChange={setCohortId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {cohorts.map((c: any) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Experto</Label>
            <Select value={expertId} onValueChange={setExpertId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {experts?.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Tipo</Label>
            <Select value={type} onValueChange={(v) => setType(v as any)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="workshop">Workshops</SelectItem>
                <SelectItem value="mentoring">Mentorías</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Switch id="norec" checked={onlyNoRec} onCheckedChange={setOnlyNoRec} />
            <Label htmlFor="norec" className="text-sm">Solo sin grabación</Label>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader><CardTitle className="text-base">Sesiones del período</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead>Cohorte / Organización</TableHead>
                  <TableHead>Experto</TableHead>
                  <TableHead>Duración</TableHead>
                  <TableHead>Grabación</TableHead>
                  <TableHead>Entregables</TableHead>
                  <TableHead>Liquidación</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">Cargando…</TableCell></TableRow>}
                {!isLoading && rows.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">No hay sesiones para los filtros seleccionados.</TableCell></TableRow>}
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-sm">
                      {r.scheduled_date}<br/>
                      <span className="text-xs text-muted-foreground">{formatTime(r.start_hour, r.start_minute)} – {formatTime(r.end_hour, r.end_minute)}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.type === 'workshop' ? 'default' : 'secondary'}>
                        {r.type === 'workshop' ? 'Workshop' : 'Mentoría'}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[220px]"><span className="font-medium text-sm">{r.title}</span></TableCell>
                    <TableCell className="text-sm">
                      <div>{r.cohort_name ?? '—'}</div>
                      <div className="text-xs text-muted-foreground">{r.organization_name ?? '—'}</div>
                    </TableCell>
                    <TableCell>
                      {r.expert_id ? (
                        <div className="flex items-center gap-2">
                          <Avatar className="w-7 h-7">
                            <AvatarImage src={r.expert_avatar ?? undefined} />
                            <AvatarFallback>{(r.expert_name ?? '?').slice(0, 1)}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm">{r.expert_name ?? '—'}</span>
                        </div>
                      ) : <span className="text-xs text-muted-foreground">Sin experto</span>}
                    </TableCell>
                    <TableCell className="text-sm font-mono">{r.duration_hours.toFixed(2)}h</TableCell>
                    <TableCell>
                      {r.has_recording ? (
                        <a href={r.recording_link!} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-success text-sm hover:underline">
                          <CheckCircle2 className="w-4 h-4" /> Ver <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <Badge variant="destructive" className="gap-1"><AlertCircle className="w-3 h-3" /> Sin grabación</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.requires_deliverable ? (
                        <span className={r.deliverables_received < r.deliverables_expected ? 'text-warning font-medium' : 'text-success'}>
                          {r.deliverables_received} / {r.deliverables_expected}
                        </span>
                      ) : <span className="text-xs text-muted-foreground">N/A</span>}
                    </TableCell>
                    <TableCell>
                      {r.payout_id ? (
                        <Badge variant="outline" className="text-xs"><ReceiptIcon className="w-3 h-3" /> {r.payout_status}</Badge>
                      ) : <span className="text-xs text-muted-foreground">Pendiente</span>}
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => setDetail(r)}><FileText className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.title}</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm">
                <div><strong>Fecha:</strong> {detail.scheduled_date} · {formatTime(detail.start_hour, detail.start_minute)}–{formatTime(detail.end_hour, detail.end_minute)} ({detail.duration_hours.toFixed(2)}h)</div>
                <div><strong>Tipo:</strong> {detail.type}</div>
                <div><strong>Cohorte:</strong> {detail.cohort_name} — <strong>Organización:</strong> {detail.organization_name}</div>
                <div><strong>Experto:</strong> {detail.expert_name ?? '—'}</div>
                <div><strong>Grabación:</strong> {detail.has_recording ? <a className="text-primary underline" href={detail.recording_link} target="_blank" rel="noreferrer">Abrir</a> : <span className="text-destructive">No subida</span>}</div>
                <div><strong>Link conexión:</strong> {detail.connection_link ? <a className="text-primary underline" href={detail.connection_link} target="_blank" rel="noreferrer">Abrir</a> : '—'}</div>
                {detail.requires_deliverable && (
                  <div><strong>Entregables:</strong> {detail.deliverables_received} recibidos de {detail.deliverables_expected} esperados</div>
                )}
                {detail.payout_id && (
                  <div><strong>Liquidación:</strong> <Link className="text-primary underline" to={`/payouts/${detail.payout_id}`}>Ver ({detail.payout_status})</Link></div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
